'use client'

import Image from 'next/image'
import { useStore } from '@/hooks/useStore'
import { useIsMobile } from '@/hooks/useIsMobile'
import { FiHome, FiFolder, FiLayers, FiUser, FiMail, FiSun, FiMoon, FiX } from 'react-icons/fi'

export default function Sidebar() {
  const { activeSection, setActiveSection, theme, toggleTheme, sidebarOpen, setSidebarOpen } = useStore()
  const isMobile = useIsMobile()

  const menuItems = [
    { id: 'home', label: 'Home', icon: FiHome },
    { id: 'work', label: 'Why Us?', icon: FiLayers },
    { id: 'about', label: 'About', icon: FiUser },
    { id: 'projects', label: 'Projects', icon: FiFolder },
    { id: 'contact', label: 'Contact', icon: FiMail },
  ]

  return (
    <aside
      className={`w-[82vw] max-w-72 md:w-64 glass-nav h-dvh fixed top-0 left-0 z-50 flex flex-col justify-between py-8 px-6 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
        sidebarOpen ? 'translate-x-0 shadow-[20px_0_60px_rgba(0,0,0,0.9)] border-r border-glass/15 bg-bg-elevated/95' : '-translate-x-full border-r border-transparent'
      }`}
    >
      {/* Brand Logo - Official Icon Mark */}
      <div className="flex items-center justify-between">
        <Image
          src="/favicon.png"
          alt="COR-PIX Studio Logo"
          width={44}
          height={44}
          className="h-9 w-9 rounded-xl object-contain drop-shadow-[0_0_12px_rgba(163,230,53,0.25)] select-none"
          priority
        />
        <button
          onClick={() => setSidebarOpen(false)}
          aria-label="Close navigation menu"
          className="md:hidden w-8 h-8 rounded-full flex items-center justify-center bg-glass/5 border border-glass/10 text-text-body hover:text-text-title hover:bg-glass/10 transition-colors cursor-pointer"
        >
          <FiX className="w-4 h-4" />
        </button>
      </div>

      {/* Menu Links - High Fidelity Mockup Match */}
      <nav className="flex flex-col gap-2.5">
        {menuItems.map((item) => {
          const Icon = item.icon
          const isActive = activeSection === item.id
          return (
            <button
              key={item.id}
              onClick={() => {
                setActiveSection(item.id)
                // On mobile all sections are stacked in normal scroll flow rather than
                // swapped one-at-a-time, so activeSection alone won't move the viewport —
                // scroll to the section too, or the tap would appear to do nothing.
                if (isMobile) {
                  document.getElementById(`section-${item.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
                setSidebarOpen(false)
              }}
              className={`w-full flex items-center justify-between py-3 px-4 rounded-xl text-left transition-all duration-300 border cursor-pointer ${
                isActive 
                  ? 'bg-glass/[0.06] border-glass/[0.08] text-text-title shadow-[inset_0_1px_2px_rgba(255,255,255,0.05)]' 
                  : 'bg-transparent border-transparent text-text-body hover:text-text-title hover:bg-glass/[0.04]'
              }`}
            >
              <div className="flex items-center gap-3">
                <Icon className={`w-4 h-4 transition-colors duration-300 ${isActive ? 'text-brand-green' : 'text-text-body'}`} />
                <span className="font-sans text-sm font-semibold">{item.label}</span>
              </div>
              {isActive && (
                <div className="w-1.5 h-1.5 rounded-full bg-brand-green shadow-[0_0_8px_rgba(163,230,53,0.8)]" />
              )}
            </button>
          )
        })}
      </nav>

      {/* Footer Controls */}
      <div className="flex flex-col gap-6">
        {/* Theme Toggle */}
        <div className="flex items-center justify-between py-3 px-4 rounded-xl bg-glass/[0.03] border border-glass/5">
          <div className="flex items-center gap-3">
            {theme === 'dark' ? (
              <FiMoon className="w-4 h-4 text-text-body" />
            ) : (
              <FiSun className="w-4 h-4 text-brand-green" />
            )}
            <span className="font-sans text-xs font-semibold text-text-body">Theme</span>
          </div>
          <button
            onClick={toggleTheme}
            role="switch"
            aria-checked={theme === 'light'}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            className="w-9 h-5 rounded-full p-0.5 transition-colors duration-300 relative bg-glass/10"
            style={{ backgroundColor: theme === 'light' ? 'rgba(163,230,53,0.2)' : '' }}
          >
            <div
              className={`w-4 h-4 rounded-full shadow transition-all duration-300 ${
                theme === 'light' ? 'translate-x-4 bg-brand-green' : 'translate-x-0 bg-text-subtle'
              }`}
            />
          </button>
        </div>

        {/* Copyright */}
        <div className="text-[10px] font-sans text-text-subtle leading-normal">
          <p>© COR-PIX Studio</p>
          <p className="mt-0.5">All rights reserved.</p>
        </div>
      </div>
    </aside>
  )
}
